import React, { Component } from 'react';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { decode as jwtDecode } from 'jwt-simple';
import localStore from 'store';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';

import sessionTokenQuery from '../graphql/sessionTokenQuery';
import client from '../apollo';
import APP_PATH from '../constants/paths';
import viewerQuery from '../graphql/viewerQuery';
import { setUser, clearUser } from '../data/user/actions';
import { setNextPath } from '../data/nextPath/actions';
import { setAuthToken, resetAuthToken } from '../data/authToken/actions';
import URLPATH from '../constants/urlPaths';

const requireAuth = ComposedComponent => {
  class Authentication extends Component {
    isLoggedOut() {
      const { match } = this.props;
      const nowInSeconds = Math.round(new Date().getTime() / 1000);
      let token = localStore.get('jwt');
      const linkToken = match.params.token;
      if (!isEmpty(linkToken)) {
        token = linkToken;
      }
      if (isEmpty(token)) {
        return true;
      }
      const decoded = jwtDecode(token, null, true);
      const { exp } = decoded;
      return nowInSeconds > exp;
    }

    refetchUser() {
      const { signInUser } = this.props;
      client
        .query({
          query: viewerQuery,
        })
        .then(({ data }) => {
          const user = get(data, 'viewer.user');
          signInUser(user);
        });
    }

    setRedirect() {
      const { location } = this.props;
      const appPath = location.pathname;
      if (Authentication.validatePath(appPath)) {
        localStorage.setItem('external_path', appPath);
      }
    }

    static validatePath(path) {
      if (!isEmpty(path)) {
        const regex = /([^/].+?(?=\/|$))/;
        const regexResult = regex.exec(path);
        if (
          !isEmpty(regexResult)
          && URLPATH.PATH.includes(regexResult[0].toLowerCase())
        ) {
          return true;
        }
      }
      return false;
    }

    componentDidUpdate(prevProps) {
      const {
        data,
        match,
        signInUser,
        setAuthTokenAvailability,
      } = this.props;

      if (
        prevProps.data.sessionToken !== data.sessionToken
        && !isEmpty(match)
        && !isEmpty(match.params)
      ) {
        const { sessionToken } = data;
        if (!isEmpty(sessionToken) && !isEmpty(sessionToken.viewer)) {
          signInUser(sessionToken.viewer.user);
          localStore.set('jwt', sessionToken.accessToken);
          localStore.set('tokenUser', true);
          setAuthTokenAvailability({});
        }
      }
    }

    UNSAFE_componentWillMount() {
      const {
        match,
        authenticated,
        authToken,
        setNextPath: setNextPathProp,
        history,
      } = this.props;
      let token = localStore.get('jwt');
      const { params } = match;
      const noTokenCondition = isEmpty(params) || (!isEmpty(params) && isEmpty(params.token));

      const isAuthToken = isEmpty(token) && !isEmpty(params) && !isEmpty(params.token);

      if (
        !authenticated
        && !(token === undefined)
        && noTokenCondition
      ) {
        this.refetchUser();
      }
      if (isAuthToken) {
        token = params.token;
      }

      if (
        (!authenticated
          && token === undefined
          && noTokenCondition)
        || this.isLoggedOut()
        || (authToken && noTokenCondition)
      ) {
        if (!window.location.href.includes('loginCallback')) {
          setNextPathProp(history.location.pathname);
          this.setRedirect();
          history.push(APP_PATH.LOGIN);
        } else {
          setNextPathProp(APP_PATH.REDIRECT_URL);
        }
      }
    }

    UNSAFE_componentWillUpdate(nextProps, nextState) {
      const { history } = this.props;
      if (
        ((!isEmpty(nextState) && isEmpty(nextState.data.user))
          || this.isLoggedOut())
        && (isEmpty(nextProps.match.params)
          || (!isEmpty(nextProps.match.params)
            && isEmpty(nextProps.match.params.token)))
      ) {
        history.push(APP_PATH.LOGIN);
      }
    }

    componentDidMount() {
      const {
        match,
        authToken,
        signOutUser,
        resetAuthTokenAvailability,
        history,
      } = this.props;
      const { params } = match;
      const tokenUser = localStore.get('tokenUser');
      const noTokenCondition = isEmpty(params) || (!isEmpty(params) && isEmpty(params.token));

      if ((authToken || tokenUser) && noTokenCondition) {
        signOutUser();
        client.resetStore();
        localStore.remove('jwt');
        localStore.remove('tokenUser');
        resetAuthTokenAvailability({});
        history.push(APP_PATH.LOGIN);
      }
    }

    render() {
      return React.createElement(ComposedComponent, this.props);
    }
  }

  const mapStateToProps = state => {
    const { user, authToken } = state.data;
    return {
      authenticated: !isEmpty(user),
      authToken: isEmpty(authToken) ? false : authToken,
    };
  };

  const mapDispatchToProps = dispatch => ({
    signInUser: user => {
      dispatch(setUser(user));
    },
    signOutUser: () => {
      dispatch(clearUser());
    },
    setNextPath: pathName => {
      dispatch(setNextPath(pathName));
    },
    setAuthTokenAvailability: () => {
      dispatch(setAuthToken());
    },
    resetAuthTokenAvailability: () => {
      dispatch(resetAuthToken());
    },
  });

  const data = graphql(sessionTokenQuery, {
    options: props => {
      let token = null;
      const { params } = props.match;
      if (isEmpty(props.user) && !isEmpty(params.token)) {
        token = params.token;
      }

      return {
        variables: { token },
        fetchPolicy: 'network-only',
      };
    },
  });

  Authentication.contextTypes = {
    router: PropTypes.object,
  };

  Authentication.propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        token: PropTypes.string,
      }),
    }).isRequired,
    signInUser: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    data: PropTypes.shape({
      sessionToken: PropTypes.shape({
        viewer: PropTypes.shape({
          user: PropTypes.object,
        }),
        accessToken: PropTypes.string,
      }),
    }).isRequired,
    setAuthTokenAvailability: PropTypes.func.isRequired,
    authenticated: PropTypes.bool.isRequired,
    authToken: PropTypes.bool.isRequired,
    setNextPath: PropTypes.func.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
      location: PropTypes.shape({
        pathname: PropTypes.string,
      }),
    }).isRequired,
    signOutUser: PropTypes.func.isRequired,
    resetAuthTokenAvailability: PropTypes.func.isRequired,
  };

  return compose(
    data,
    connect(
      mapStateToProps,
      mapDispatchToProps
    )
  )(Authentication);
};

export default requireAuth;
